"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.accountBridge = void 0;
const ledger_stacks_1 = __importDefault(require("@zondax/ledger-stacks"));
const c32check_1 = require("c32check");
const network_1 = require("@stacks/network");
const bignumber_js_1 = require("bignumber.js");
const bn_js_1 = __importDefault(require("bn.js"));
const rxjs_1 = require("rxjs");
const invariant_1 = __importDefault(require("invariant"));
const errors_1 = require("@ledgerhq/errors");
const cryptoassets_1 = require("@ledgerhq/cryptoassets");
const errors_2 = require("../errors");
const transactions_1 = require("@stacks/transactions");
const jsHelpers_1 = require("../../../bridge/jsHelpers");
const jsHelpers_2 = require("@ledgerhq/coin-framework/bridge/jsHelpers");
const api_types_1 = require("./utils/api.types");
const misc_1 = require("./utils/misc");
const api_1 = require("./utils/api");
const utils_1 = require("../../filecoin/bridge/utils/utils");
const deviceAccess_1 = require("../../../hw/deviceAccess");
const utils_2 = require("../utils");
const account_1 = require("../../../account");
const operation_1 = require("../../../operation");
const addresses_1 = require("./utils/addresses");
const receive = (0, jsHelpers_1.makeAccountBridgeReceive)();
const createTransaction = () => {
    return {
        family: "stacks",
        recipient: "",
        amount: new bignumber_js_1.BigNumber(0),
        network: "mainnet",
        anchorMode: transactions_1.AnchorMode.Any,
        useAllAmount: false,
    };
};
const sync = (0, jsHelpers_1.makeSync)({ getAccountShape: misc_1.getAccountShape });
const broadcast = ({ signedOperation: { operation, signature, rawData }, }) => __awaiter(void 0, void 0, void 0, function* () {
    (0, invariant_1.default)(operation, "StacksOperation expected");
    const tx = yield (0, misc_1.getTxToBroadcast)(operation, signature, rawData !== null && rawData !== void 0 ? rawData : {});
    const hash = yield (0, api_1.broadcastTx)(tx);
    const result = (0, operation_1.patchOperationWithHash)(operation, hash);
    return result;
});
const getTransactionStatus = (a, t) => __awaiter(void 0, void 0, void 0, function* () {
    const errors = {};
    const warnings = {};
    const { spendableBalance } = a;
    const { address } = (0, utils_1.getAddress)(a);
    const { recipient, useAllAmount, fee } = t;
    const { memo } = t;
    let { amount } = t;
    if (!recipient) {
        errors.recipient = new errors_1.RecipientRequired();
    }
    else if (!(0, addresses_1.validateAddress)(recipient).isValid) {
        errors.recipient = new errors_1.InvalidAddress();
    }
    else if (address === recipient) {
        errors.recipient = new errors_1.InvalidAddressBecauseDestinationIsAlsoSource();
    }
    else if (!fee || fee.eq(0)) {
        errors.gas = new errors_1.FeeNotLoaded();
    }
    const estimatedFees = fee || new bignumber_js_1.BigNumber(0);
    const totalSpent = useAllAmount ? spendableBalance : amount.plus(estimatedFees);
    amount = useAllAmount ? spendableBalance.minus(estimatedFees) : amount;
    if (amount.lte(0))
        errors.amount = new errors_1.AmountRequired();
    if (totalSpent.gt(spendableBalance))
        errors.amount = new errors_1.NotEnoughBalance();
    if (memo && memo.length > 34)
        errors.transaction = new errors_2.StacksMemoTooLong();
    return {
        errors,
        warnings,
        estimatedFees,
        amount,
        totalSpent,
    };
});
const estimateMaxSpendable = ({ account, parentAccount, transaction, }) => __awaiter(void 0, void 0, void 0, function* () {
    const a = (0, account_1.getMainAccount)(account, parentAccount);
    const { id: accountId, spendableBalance } = a;
    const { xpubOrAddress: xpub } = (0, account_1.decodeAccountId)(accountId);
    const dummyTx = Object.assign(Object.assign(Object.assign({}, createTransaction()), transaction), { recipient: (0, cryptoassets_1.getAbandonSeedAddress)(a.currency.id), useAllAmount: true });
    // Compute fees
    const { recipient, anchorMode, memo, amount } = dummyTx;
    const network = api_types_1.StacksNetwork[dummyTx.network] || new network_1.StacksMainnet();
    const options = {
        recipient,
        anchorMode,
        memo,
        network,
        publicKey: xpub,
        amount: new bn_js_1.default(amount.toFixed()),
    };
    const tx = yield (0, transactions_1.makeUnsignedSTXTokenTransfer)(options);
    const fee = yield (0, transactions_1.estimateTransfer)(tx);
    const diff = spendableBalance.minus(new bignumber_js_1.BigNumber(fee.toString()));
    return diff.gte(0) ? diff : new bignumber_js_1.BigNumber(0);
});
const prepareTransaction = (a, t) => __awaiter(void 0, void 0, void 0, function* () {
    const { id: accountID, spendableBalance, pendingOperations } = a;
    const { recipient, useAllAmount } = t;
    const { xpubOrAddress: xpub } = (0, account_1.decodeAccountId)(accountID);
    const patch = {};
    if (xpub && recipient && (0, addresses_1.validateAddress)(recipient).isValid) {
        const { anchorMode, memo, amount } = t;
        const network = api_types_1.StacksNetwork[t.network] || new network_1.StacksMainnet();
        // Check if recipient is valid
        const options = {
            recipient,
            anchorMode,
            memo,
            network,
            publicKey: xpub,
            amount: new bn_js_1.default(amount.toFixed()),
        };
        const tx = yield (0, transactions_1.makeUnsignedSTXTokenTransfer)(options);
        const addressVersion = network.version === transactions_1.TransactionVersion.Mainnet
            ? transactions_1.AddressVersion.MainnetSingleSig
            : transactions_1.AddressVersion.TestnetSingleSig;
        const senderAddress = (0, c32check_1.c32address)(addressVersion, tx.auth.spendingCondition.signer);
        const fee = yield (0, transactions_1.estimateTransfer)(tx);
        patch.fee = new bignumber_js_1.BigNumber(fee.toString());
        patch.nonce = yield (0, misc_1.findNextNonce)(senderAddress, pendingOperations);
        if (useAllAmount)
            patch.amount = spendableBalance.minus(patch.fee);
    }
    return (0, jsHelpers_2.defaultUpdateTransaction)(t, patch);
});
const signOperation = ({ account, deviceId, transaction, }) => (0, deviceAccess_1.withDevice)(deviceId)(transport => new rxjs_1.Observable(o => {
    function main() {
        return __awaiter(this, void 0, void 0, function* () {
            const { id: accountId } = account;
            const { address, derivationPath } = (0, utils_1.getAddress)(account);
            const { xpubOrAddress: xpub } = (0, account_1.decodeAccountId)(accountId);
            const { recipient, fee, anchorMode, network, memo, amount, nonce } = transaction;
            if (!xpub) {
                throw new errors_1.InvalidAddress();
            }
            if (!fee) {
                throw new errors_1.FeeNotLoaded();
            }
            if (!nonce) {
                throw new errors_1.InvalidNonce();
            }
            const blockstack = new ledger_stacks_1.default(transport);
            const options = {
                amount: new bn_js_1.default(amount.toFixed()),
                recipient,
                anchorMode,
                network: api_types_1.StacksNetwork[network],
                memo,
                publicKey: xpub,
                fee: new bn_js_1.default(fee.toFixed()),
                nonce: new bn_js_1.default(nonce.toFixed()),
            };
            const tx = yield (0, transactions_1.makeUnsignedSTXTokenTransfer)(options);
            const serializedTx = tx.serialize();
            o.next({
                type: "device-signature-requested",
            });
            // Sign by device
            const result = yield blockstack.sign((0, utils_2.getPath)(derivationPath), serializedTx);
            (0, utils_2.throwIfError)(result);
            o.next({
                type: "device-signature-granted",
            });
            const txHash = "";
            // build signature on the correct format
            const signature = `${result.signatureVRS.toString("hex")}`;
            const operation = {
                id: (0, operation_1.encodeOperationId)(accountId, txHash, "OUT"),
                hash: txHash,
                type: "OUT",
                senders: [address],
                recipients: [recipient],
                accountId,
                value: amount.plus(fee),
                fee,
                blockHash: null,
                blockHeight: null,
                date: new Date(),
                transactionSequenceNumber: nonce.toNumber(),
                extra: {
                    memo,
                },
            };
            o.next({
                type: "signed",
                signedOperation: {
                    operation,
                    signature,
                    rawData: {
                        xpub,
                        network,
                        anchorMode,
                    },
                },
            });
        });
    }
    main().then(() => o.complete(), e => o.error(e));
}));
exports.accountBridge = {
    createTransaction,
    updateTransaction: jsHelpers_2.defaultUpdateTransaction,
    getTransactionStatus,
    prepareTransaction,
    estimateMaxSpendable,
    signOperation,
    sync,
    receive,
    broadcast,
};
//# sourceMappingURL=account.js.map